import { Box, Section, Stack } from "@vinomofo/components";
import { useEffect, useRef, useState } from "react";

import { BadgeStack } from "../badge-stack";
import { GatsbyImage } from "gatsby-plugin-image";
import { OfferDetailsMixedProps } from "../offer-details";
import { OfferHeroCard } from "./OfferHeroCard";
import { OfferHeroImageLarge } from "./OfferHeroImageLarge";
import { OfferHeroImageMixedWide } from "./OfferHeroImageMixedWide";
import { Roundel } from "../roundel";
import { VinoDataOffer } from "../../types/vino-data-types";
import styled from "styled-components";

const StyledGatsbyImage = styled(GatsbyImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export interface OfferHeroMixedProps {
  details: OfferDetailsMixedProps;
  offer: VinoDataOffer;
}

export const OfferHeroMixed = ({
  details,
  offer,
}: OfferHeroMixedProps): JSX.Element => {
  const {
    productId,
    endsAt,
    price,
    packRRP,
    priceDescription,
    shortDescription,
    discountPercentage,
    discountDollarValue,
    discountDisplay,
    region,
    style,
    showPriceDrop,
    showCountdown,
    awards,
    heroImage,
    urgencyMessage,
    showDiscountBadge,
    showAwardBadges,
    isSoldOut,
    backgroundColor,
    wideHeroImage,
    largeHeroImage,
    name,
    roundelsImage,
  } = details;

  const heroImageRef = useRef<HTMLElement>(null);

  const [imageHeight, setImageHeight] = useState(0);

  useEffect(() => {
    if (heroImageRef.current) setImageHeight(heroImageRef.current.clientHeight);
  }, [heroImageRef?.current?.clientHeight]);

  return (
    <Section backgroundColor={backgroundColor} pb={["20px", "60px"]}>
      <Box display={["block", "block", "flex"]} justifyContent="space-between">
        <Box
          flexBasis={["100%", "100%", "65.5172413793%"]}
          paddingTop={["20px", "20px", "40px"]}
          alignSelf="flex-start"
          position="relative"
        >
          <Stack>
            {showAwardBadges && (
              <Box
                position="absolute"
                zIndex={1}
                paddingTop={["20px", "0", "5px"]}
              >
                <BadgeStack
                  badges={awards}
                  gap={["-8px", "12px"]}
                  horizontal
                  limit={5}
                />
              </Box>
            )}
            <Box
              textAlign="center"
              width="100%"
              display={["block", "none", "none"]}
            >
              {heroImage && <StyledGatsbyImage image={heroImage} alt={name} />}
            </Box>

            <Box
              textAlign="center"
              width="100%"
              display={["none", "block", "none"]}
            >
              {largeHeroImage ? (
                <OfferHeroImageLarge
                  image={largeHeroImage}
                  discountPercent={discountPercentage}
                  discountDisplay={discountDisplay}
                  discountDollarValue={discountDollarValue}
                  showDiscountBadge={showDiscountBadge}
                  isAvailable={!isSoldOut}
                  display={["none", "block", "none"]}
                />
              ) : (
                <GatsbyImage image={heroImage} alt={name} />
              )}
            </Box>

            {heroImage && (
              <Box
                textAlign="center"
                width="100%"
                display={["none", "none", "block"]}
              >
                {wideHeroImage ? (
                  <>
                    {/* <OfferHeroImageMixedWide
                      image={wideHeroImage}
                      display={["none", "none", "block"]}
                      ref={heroImageRef}
                    /> */}
                  </>
                ) : (
                  <GatsbyImage image={heroImage} alt={name} />
                )}
              </Box>
            )}
          </Stack>

          {roundelsImage && !wideHeroImage && (
            <Roundel
              width={["110px", "120px", "160px"]}
              height={["110px", "120px", "160px"]}
              isExpired={isSoldOut}
              image={roundelsImage}
              bottom="10px"
              left={["auto", "auto", "auto"]}
              right={["5%", "5%", "5%"]}
              zIndex="1"
            />
          )}
        </Box>
        <Box
          flexBasis={["100%", "100%", "31.034%"]}
          maxWidth={["100%", "100%", "360px"]}
          paddingTop={["0", "30px", "80px"]}
        >
          <OfferHeroCard
            productId={productId}
            shortDescription={shortDescription}
            price={offer?.isMixed ? price : price * (offer?.packSize || 1)}
            rrp={packRRP}
            priceDescription={priceDescription}
            region={region}
            style={style}
            endsAt={endsAt}
            showCountdown={showCountdown}
            showPriceDrop={showPriceDrop}
            urgencyMessage={urgencyMessage}
            isSoldOut={isSoldOut}
            showDiscountBadge={showDiscountBadge}
            discountPercentage={discountPercentage}
            backgroundColor={backgroundColor}
            discountDisplay={discountDisplay}
            discountDollarValue={discountDollarValue}
            name={name}
            offer={offer}
            hideIconVarietal={true}
          />
        </Box>
      </Box>
    </Section>
  );
};
